import {Badge, FileIcon, Table, Typography, nodeColumn} from '@sollapay/ui';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {DateCell} from '@/components';

import {DocumentActionsBar} from './DocumentActionsBar';
import {UploaderCell} from './UploaderCell';
import {getDocumentTypeLabel} from '../utils';

import type {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';
import type {FC, ReactNode} from 'react';

interface DocumentTableRow {
  id: string;
  fileName: string;
  fileExtension: string;
  fileSize: string;
  documentType: DocumentType;
  uploadDate: string;
  lastUpdated: string;
  uploadedBy?: {
    name: string;
    role: string;
    avatarUrl?: string;
  };
  documentContext?: DocumentContext;
}

interface DocumentsTableProps {
  documents: DocumentTableRow[];
  entityType: DocumentEntityType;
  entityId: string;
  documentContext: DocumentContext;
  hiddenColumns?: string[];
  emptyState?: ReactNode;
  onDeleted?: () => void;
  onError?: (error: unknown) => void;
  /** When provided, gates delete per row instead of uniformly for the whole table. */
  isRowDeletable?: (row: DocumentTableRow) => boolean;
}

export const DocumentsTable: FC<DocumentsTableProps> = ({
  documents,
  entityType,
  entityId,
  documentContext,
  hiddenColumns,
  emptyState,
  onDeleted,
  onError,
  isRowDeletable
}) => {
  const {t} = useTranslation('common');
  const isRowReadOnly = useCallback(
    (row: DocumentTableRow) => !(onDeleted && (!isRowDeletable || isRowDeletable(row))),
    [onDeleted, isRowDeletable]
  );
  const columns = useMemo(
    () => [
      nodeColumn<DocumentTableRow>({
        id: 'fileName',
        header: t('documentsTable.columns.fileName'),
        maxWidth: 200,

        accessorFn: row => row.fileName,
        cell: row => (
          <div className="flex items-center gap-3">
            <FileIcon ext={row.fileExtension.replace('.', '').toUpperCase()} size="md" />
            <Typography size="sm" weight="medium">
              {row.fileName}
            </Typography>
          </div>
        )
      }),
      nodeColumn<DocumentTableRow>({
        id: 'documentType',
        header: t('documentsTable.columns.documentType'),

        accessorFn: row => row.documentType,
        cell: row => (
          <Badge variant="neutral" size="sm">
            {getDocumentTypeLabel(row.documentType, t)}
          </Badge>
        )
      }),
      nodeColumn<DocumentTableRow>({
        id: 'fileSize',
        header: t('documentsTable.columns.fileSize'),

        accessorFn: row => row.fileSize,
        cell: row => (
          <Typography size="sm" color="secondary">
            <span dir="ltr">{row.fileSize}</span>
          </Typography>
        )
      }),
      nodeColumn<DocumentTableRow>({
        id: 'lastUpdated',
        header: t('documentsTable.columns.lastUpdated'),

        accessorFn: row => row.lastUpdated,
        cell: row => <DateCell date={row.lastUpdated} />
      }),
      nodeColumn<DocumentTableRow>({
        id: 'uploadDate',
        header: t('documentsTable.columns.uploadDate'),

        accessorFn: row => row.uploadDate,
        cell: row => <DateCell date={row.uploadDate} />
      }),
      ...(!hiddenColumns?.includes('uploadedBy')
        ? [
            nodeColumn<DocumentTableRow>({
              id: 'uploadedBy',
              header: t('documentsTable.columns.uploadedBy'),

              accessorFn: row => row.uploadedBy?.name ?? '',
              cell: row => <UploaderCell uploader={row.uploadedBy} />
            })
          ]
        : []),
      nodeColumn<DocumentTableRow>({
        id: 'actions',
        header: '',
        width: 'min',
        enableSorting: false,
        cell: row => (
          <DocumentActionsBar
            documentId={row.id}
            entityType={entityType}
            entityId={entityId}
            documentContext={row.documentContext ?? documentContext}
            onDeleted={!isRowReadOnly(row) ? onDeleted : undefined}
            onError={onError}
          />
        )
      })
    ],
    [t, entityType, entityId, documentContext, hiddenColumns, onDeleted, onError, isRowReadOnly]
  );

  return (
    <Table
      columns={columns}
      data={documents}
      emptyState={emptyState}
      emptyStateText={t('documentsTable.emptyState')}
      isRowPinned={isRowReadOnly}
    />
  );
};

export type {DocumentTableRow, DocumentsTableProps};
